import { FC, useCallback, useEffect, useState } from 'react';
import { useSubscription } from '@apollo/client';
import { Image, VStack } from '@chakra-ui/react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import Heading from 'components/_core/Heading';
import ProgressBar from 'components/_core/ProgressBar';
import Text from 'components/_core/Text';
import { StepProps } from 'components/Stepper/types';
import useCreditCards from 'features/PaymentCards/hooks/useCreditCards';
import * as Layout from 'layouts/StepperLayout';

import ChillImage from '../../../../../../../../../assets/brand/chill.png';
import { VirtualCardAutomaticallyOrderedDocument } from '../../../../hooks/graphql/subscriptions/onVirtualCardAutomaticallyOrdered.gql';
import locales from './locales';

const LOADING_TIMEOUT_IN_SECONDS = 10;
const START_LOADING_DELAY_IN_SECONDS = 1;

const AutomaticVirtualCardLoader: FC<StepProps> = ({ goNext }) => {
  const { companyProfileId = '' } = useCompanyContext();

  const [loadingRatioTarget, setLoadingRatioTarget] = useState<number>(0);

  const { refetch: refetchPaymentCards } = useCreditCards({
    fetchPolicy: 'cache-only',
  });

  const goToAutomaticVirtualCardPage = useCallback(() => {
    goNext();
  }, [goNext]);

  useSubscription(VirtualCardAutomaticallyOrderedDocument, {
    onData: async ({ data: { data } }) => {
      if (data === undefined) {
        return;
      }
      await refetchPaymentCards();
      goToAutomaticVirtualCardPage();
    },
    skip: !companyProfileId,
    variables: {
      companyProfileId,
    },
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      goToAutomaticVirtualCardPage();
    }, LOADING_TIMEOUT_IN_SECONDS * 1000);

    const startDelay = setTimeout(() => {
      setLoadingRatioTarget(1);
    }, START_LOADING_DELAY_IN_SECONDS * 1000);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(startDelay);
    };
  }, [goToAutomaticVirtualCardPage]);

  return (
    <Layout.Root>
      <VStack
        alignItems="center"
        marginX="auto"
        paddingY="space-48"
        spacing="space-16"
      >
        <Image boxSize="180px" marginX="auto" src={ChillImage} />

        <Heading size="lg">{locales.title}</Heading>
        <ProgressBar
          barProps={{
            transition: 'ease-out 1s',
            transitionDuration: `${
              LOADING_TIMEOUT_IN_SECONDS - START_LOADING_DELAY_IN_SECONDS
            }s`,
          }}
          marginTop="space-16"
          ratio={loadingRatioTarget}
          width="175px"
        />
        <Text variant="light">{locales.subtitle}</Text>
      </VStack>
    </Layout.Root>
  );
};

export default AutomaticVirtualCardLoader;
